import React, { useState, useEffect } from 'react';
import { PREVIEW, generateSurfaceUrl } from '../utils/environments';
import { snakeCaseToTitleCase } from '../utils/api_utils';
import CustomLoader from '../../features/common/CustomLoader';
import ToggleButton from './ToggleButton';
import Swal from 'sweetalert2';
import voca from 'voca';

const PreviewPublishContent = ({
  projectId,
  projectDetail,
  versions,
  selVersion,
  isPreviewPublishing,
  handleVersionChange,
  publishApp,
  actions,
  version,
  variant = 'table',
  panelId,
  embedded = false,
}) => {
  const [debugModeState, setDebugModeState] = useState({});
  const [cleanPublishState, setCleanPublishState] = useState({});

  let { seoName, debugMode = [] } = projectDetail;

  useEffect(() => {
    const initialDebugState = {};
    const initialCleanState = {};

    if (debugMode) {
      initialDebugState[PREVIEW] = {
        isEnable: debugMode.includes(PREVIEW),
        environment: PREVIEW,
      };
    }

    initialCleanState[PREVIEW] = true;

    setDebugModeState(initialDebugState);
    setCleanPublishState(initialCleanState);
  }, [debugMode]);

  const url = generateSurfaceUrl(PREVIEW, seoName);

  const generateProjectURL = () => {
    return (
      <>
        <i className="wb webconnect-box-arrow-in-up-right" title="Open link in new tab"></i>
        <a
          className="icon-link"
          style={{ color: '#1F2041' }}
          href={url}
          target="_blank"
          rel="noreferrer"
          title="Open link in new tab"
        >
          {url}
        </a>
      </>
    );
  };

  const handlePublishClick = async () => {
    try {
      const res = await publishApp(PREVIEW, 'Preview', cleanPublishState[PREVIEW]);
      if (res && res.success) {
        console.log('App published in Preview successfully!');
      }
    } catch (error) {
      console.error('Publish failed:', error);
    }
  };

  const handleDebugModeToggle = async (envType) => {
    const currentState = debugModeState[envType] ? debugModeState[envType].isEnable : false;

    const newState = !currentState;

    const heading = `Are you sure to ${
      newState ? 'enable' : 'disable'
    } debug mode for ${voca.titleCase(envType)}?`;

    const description = newState
      ? 'It will start displaying logs in the browser console window.'
      : 'It will stop displaying logs in the browser console window.';

    const result = await Swal.fire({
      title: heading,
      text: description,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newState ? '#3085d6' : '#d33',
      cancelButtonColor: '#757f91',
      confirmButtonText: `Yes, ${newState ? 'enable' : 'disable'} it!`,
    });

    if (result.isConfirmed) {
      const newStateObject = {
        isEnable: newState,
        environment: envType,
      };

      setDebugModeState({
        ...debugModeState,
        [envType]: newStateObject,
      });

      await actions.toggleDebugMode({
        projectId,
        version,
        data: newStateObject,
      });
    }
  };

  const handleCleanToggle = (envType) => {
    setCleanPublishState({
      ...cleanPublishState,
      [envType]: !cleanPublishState[envType],
    });
  };

  const appEnv = process.env.REACT_APP_ENVIRONMENT;
  const stagingDisabled = appEnv !== 'PRODUCTION';
  const disabledMessage = 'Publishing in preview on staging is disabled.';

  const previewBody = stagingDisabled ? (
    <section className="dc-production-env-card">
      <h3 className="dc-production-env-title">Preview</h3>
      <div className="dc-production-gate">{disabledMessage}</div>
    </section>
  ) : (
    <section className="dc-production-env-card">
      <h3 className="dc-production-env-title">{snakeCaseToTitleCase(PREVIEW)}</h3>
      <div className="dc-production-url-block">
        <span className="dc-production-field-label">Website URL</span>
        <a className="dc-production-url" href={url} target="_blank" rel="noreferrer" title={url}>
          <i className="wb webconnect-box-arrow-in-up-right" aria-hidden="true"></i>
          <span>{url}</span>
        </a>
      </div>
      <label className="dc-production-field">
        <span className="dc-production-field-label">Version</span>
        <select
          className="dc-production-select"
          value={selVersion[PREVIEW]}
          onChange={(event) => handleVersionChange(event, PREVIEW)}
        >
          <option value="">Select Version</option>
          {versions.map((item) => (
            <option value={item.currentVersion} key={item.currentVersion}>
              v{item.currentVersion}
            </option>
          ))}
        </select>
      </label>
      <div className="dc-production-option">
        <div>
          <div className="dc-production-option-label">Debug</div>
          <div className="dc-production-option-help">Display logs in the browser console.</div>
        </div>
        <ToggleButton
          name="enableDebugMode"
          bgColor="drapcode-violet"
          onChange={() => handleDebugModeToggle(PREVIEW)}
          isChecked={debugModeState[PREVIEW] ? debugModeState[PREVIEW].isEnable : false}
        />
      </div>
      {/* <div className="dc-production-option">
              <div className="dc-production-option-label">Clean Publish</div>
              <ToggleButton
                name="cleanPublish"
                bgColor="drapcode-violet"
                onChange={() => handleCleanToggle(PREVIEW)}
                isChecked={cleanPublishState[PREVIEW]}
              />
            </div> */}
      <div className="dc-production-actions">
        <button
          type="button"
          className="dc-production-btn dc-production-btn-secondary"
          onClick={() => window.open(url)}
        >
          <i className="wb webconnect-eye-fill" aria-hidden="true"></i>
          View
        </button>
        <button
          type="button"
          className="dc-production-btn dc-production-btn-primary"
          onClick={handlePublishClick}
          disabled={isPreviewPublishing}
        >
          <i className="wb webconnect-paper-plane-fill" aria-hidden="true"></i>
          Publish
          <CustomLoader loading={isPreviewPublishing} />
        </button>
      </div>
    </section>
  );

  if (embedded) {
    return previewBody;
  }

  if (variant === 'panel') {
    return (
      <div
        id={panelId}
        className="dc-production-panel"
        role="dialog"
        aria-modal="false"
        aria-label="Preview"
        tabIndex={-1}
      >
        <div className="dc-production-panel-intro">
          <div className="dc-production-panel-heading">
            <span className="dc-production-dot" aria-hidden="true" />
            <span>Preview</span>
          </div>
          <p className="dc-production-panel-subtitle">Publish your app for the preview.</p>
        </div>
        {previewBody}
      </div>
    );
  }

  return (
    <div className="row pt-2 pb-2">
      <div className="col-md-12 col-sm-12">
        {stagingDisabled ? (
          disabledMessage
        ) : (
          <>
            <strong>Publish your app for the preview on specific environment.</strong>

            <table className="table table-stripped table-bordered table-hover table-sm">
              <thead>
                <tr>
                  <th>Server</th>
                  <th>URL</th>
                  <th>Versions</th>
                  <th>Debug</th>
                  <th>Clean Publish</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>{snakeCaseToTitleCase(PREVIEW)}</td>

                  <td style={{ wordBreak: 'break-all' }}>
                    <p>{generateProjectURL()}</p>
                  </td>

                  <td>
                    <select
                      className="form-control"
                      value={selVersion[PREVIEW]}
                      onChange={(event) => handleVersionChange(event, PREVIEW)}
                    >
                      <option value="">Select Version</option>
                      {versions.map((item) => (
                        <option value={item.currentVersion} key={item.currentVersion}>
                          v{item.currentVersion}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <ToggleButton
                      name="enableDebugMode"
                      bgColor="drapcode-violet"
                      onChange={() => handleDebugModeToggle(PREVIEW)}
                      isChecked={debugModeState[PREVIEW] ? debugModeState[PREVIEW].isEnable : false}
                    />
                  </td>

                  <td>
                    <ToggleButton
                      name="cleanPublish"
                      bgColor="drapcode-violet"
                      onChange={() => handleCleanToggle(PREVIEW)}
                      isChecked={cleanPublishState[PREVIEW]}
                    />
                  </td>

                  <td>
                    <div className="d-flex justify-content-center my-3">
                      <button
                        type="button"
                        className="btn btn-theme-submit btn-sm"
                        onClick={() => window.open(url)}
                      >
                        <i className="wb webconnect-eye-fill"></i>&nbsp;View
                      </button>
                      &nbsp;
                      <button
                        type="button"
                        className="btn btn-theme-submit btn-sm"
                        onClick={handlePublishClick}
                        disabled={isPreviewPublishing}
                      >
                        <i className="wb webconnect-paper-plane-fill"></i>&nbsp;Publish
                        <CustomLoader loading={isPreviewPublishing} />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default PreviewPublishContent;
